import type Anthropic from '@anthropic-ai/sdk';
import type {
  Usage as AnthropicUsage,
  ContentBlock,
  ContentBlockParam,
  Message,
  MessageCreateParamsBase,
  MessageParam,
  RawMessageStreamEvent,
  Tool,
  ToolChoice,
  ToolResultBlockParam,
  ToolUnion,
} from '@anthropic-ai/sdk/resources/messages';
import { ModelBehaviorError, UserError } from '../../errors';
import logger from '../../logger';
import type {
  Model,
  ModelMetadata,
  ModelRequest,
  ModelResponse,
  ModelRetryAdvice,
  ModelRetryAdviceRequest,
  ModelSettingsReasoningEffort,
  SerializedHandoff,
  SerializedHostedTool,
  SerializedOutputType,
  SerializedTool,
} from '../../model';
import { resetCurrentSpan, setCurrentSpan } from '../../tracing/context';
import {
  createGenerationSpan,
  withGenerationSpan,
} from '../../tracing/create-spans';
import { type GenerationSpanData, Span } from '../../tracing/spans';
import type { ResponseStreamEvent } from '../../types/helpers';
import * as protocol from '../../types/protocol';
import { Usage } from '../../usage';
import {
  createLyraReplayPayload,
  getLyraReplayPayload,
  sanitizeLyraReplayProviderData,
  withLyraReplayPayload,
} from '../replay';
import { DEFAULT_ANTHROPIC_MAX_TOKENS } from './defaults';
import { getAnthropicRetryAdvice } from './retry-advice';

export type AnthropicFetch = NonNullable<
  ConstructorParameters<typeof Anthropic>[0]
>['fetch'];
export type AnthropicClient = Anthropic;

export type AnthropicThinkingBudgetMap = Partial<
  Record<Exclude<ModelSettingsReasoningEffort, null>, number>
>;

export type AnthropicMessagesModelOptions = {
  defaultMaxTokens?: number | ((model: string) => number);
  maxOutputTokens?: number | ((model: string) => number | undefined);
  thinkingBudgetTokens?: AnthropicThinkingBudgetMap;
};

type StreamingBlock = {
  index: number;
  type: string;
  text: string;
  thinking: string;
  signature?: string;
  redactedData?: string;
  id?: string;
  name?: string;
  input?: unknown;
  inputJson: string;
  partId?: string;
  providerData?: Record<string, unknown>;
};

type StreamingState = {
  id: string;
  usage: Partial<AnthropicUsage>;
  blocks: Map<number, StreamingBlock>;
  structuredOutput: boolean;
  activeBlockIndex?: number;
  rawResponse?: Record<string, unknown>;
};

const ANTHROPIC_PROVIDER_NAME = 'anthropic';

const DEFAULT_THINKING_BUDGET_TOKENS: Required<AnthropicThinkingBudgetMap> = {
  none: 0,
  minimal: 1024,
  low: 1024,
  medium: 4096,
  high: 8192,
  xhigh: 16_384,
};

export class AnthropicMessagesModel implements Model {
  #client: AnthropicClient;
  #model: string;
  #options: AnthropicMessagesModelOptions;
  metadata?: ModelMetadata;

  constructor(args: {
    client: AnthropicClient;
    model: string;
    metadata?: ModelMetadata;
    options?: AnthropicMessagesModelOptions;
  }) {
    this.#client = args.client;
    this.#model = args.model;
    this.metadata = args.metadata;
    this.#options = args.options ?? {};
  }

  getRetryAdvice(args: ModelRetryAdviceRequest): ModelRetryAdvice | undefined {
    return getAnthropicRetryAdvice(args);
  }

  async getResponse(request: ModelRequest): Promise<ModelResponse> {
    const fetchResponse = async (
      span: Span<GenerationSpanData> | undefined,
    ) => {
      if (!span) return this.#fetchResponse(request, false);

      span.spanData.model = this.#model;
      span.spanData.model_config = {
        temperature: request.modelSettings.temperature,
        top_p: request.modelSettings.topP,
        reasoning_effort: request.modelSettings.reasoning?.effort,
      };
      const response = await this.#fetchResponse(request, false);
      if (request.tracing === true) {
        span.spanData.output = [
          sanitizeLyraReplayProviderData(
            response as unknown as Record<string, unknown>,
          ),
        ];
      }
      return response;
    };

    const response = request.tracing
      ? await withGenerationSpan(fetchResponse)
      : await fetchResponse(undefined);

    return responseToModelResponse(response, request.outputType);
  }

  async *getStreamedResponse(
    request: ModelRequest,
  ): AsyncIterable<ResponseStreamEvent> {
    const span = request.tracing ? createGenerationSpan() : undefined;
    const state: StreamingState = {
      id: 'anthropic-response',
      usage: {},
      blocks: new Map(),
      structuredOutput: request.outputType !== 'text',
    };
    let completed = false;

    try {
      if (span) {
        span.start();
        setCurrentSpan(span);
      }

      const stream = await this.#fetchResponse(request, true);
      for await (const rawEvent of stream) {
        const event = rawEvent as unknown as Record<string, unknown>;
        if (event.type === 'message_start') {
          const message = asRecord(event.message);
          if (typeof message?.id === 'string') {
            state.id = message.id;
          }
          state.rawResponse = message;
          mergeUsage(state.usage, asRecord(message?.usage));
          yield { type: 'response_started', providerData: event };
        }

        yield {
          type: 'model',
          event,
          providerData: { rawModelEventSource: 'anthropic' },
        };

        for (const normalizedEvent of applyAnthropicStreamEvent(state, event)) {
          yield normalizedEvent;
          if (normalizedEvent.type === 'response_done') {
            completed = true;
          }
        }
      }

      if (!completed) {
        yield buildResponseDoneEvent(state);
      }

      if (span && state.rawResponse && request.tracing === true) {
        span.spanData.output = [
          sanitizeLyraReplayProviderData(state.rawResponse),
        ];
      }
    } catch (error) {
      if (span) {
        span.setError({
          message: 'Error streaming response',
          data: {
            error:
              request.tracing === true
                ? String(error)
                : error instanceof Error
                  ? error.name
                  : undefined,
          },
        });
      }
      throw error;
    } finally {
      if (span) {
        span.end();
        resetCurrentSpan();
      }
    }
  }

  async #fetchResponse(request: ModelRequest, stream: false): Promise<Message>;
  async #fetchResponse(
    request: ModelRequest,
    stream: true,
  ): Promise<AsyncIterable<RawMessageStreamEvent>>;
  async #fetchResponse(
    request: ModelRequest,
    stream: boolean,
  ): Promise<Message | AsyncIterable<RawMessageStreamEvent>> {
    const requestData = buildAnthropicRequest(
      this.#model,
      request,
      stream,
      this.#options,
    );

    logger.debug(
      `Calling Anthropic Messages API${
        logger.dontLogModelData
          ? ''
          : `. Request data: ${JSON.stringify(
              sanitizeLyraReplayProviderData(requestData),
              null,
              2,
            )}`
      }`,
    );

    const response = (await this.#client.messages.create(
      requestData as any,
      request.signal ? { signal: request.signal } : undefined,
    )) as unknown;

    if (!stream) {
      logger.debug(
        `Anthropic response received${
          logger.dontLogModelData
            ? ''
            : `: ${JSON.stringify(
                sanitizeLyraReplayProviderData(response),
                null,
                2,
              )}`
        }`,
      );
    }

    return response as Message | AsyncIterable<RawMessageStreamEvent>;
  }
}

function buildAnthropicRequest(
  model: string,
  request: ModelRequest,
  stream: boolean,
  options: AnthropicMessagesModelOptions,
): MessageCreateParamsBase & { stream: boolean; [key: string]: unknown } {
  const conversion = itemsToAnthropicMessages(request);
  const tools = [
    ...(request.tools ?? []).map(toolToAnthropic),
    ...(request.handoffs ?? []).map(handoffToAnthropic),
  ];
  const providerData = request.modelSettings.providerData ?? {};
  const maxTokens = resolveMaxTokens(model, request, options);
  const requestData: MessageCreateParamsBase & {
    stream: boolean;
    [key: string]: unknown;
  } = {
    model,
    messages: conversion.messages,
    system: buildSystemInstructions(conversion.system, request.outputType),
    max_tokens: maxTokens,
    stream,
    temperature: request.modelSettings.temperature,
    top_p: request.modelSettings.topP,
    tools: tools.length > 0 ? tools : undefined,
    tool_choice:
      tools.length > 0
        ? getAnthropicToolChoice(
            request.modelSettings.toolChoice,
            request.modelSettings.parallelToolCalls,
          )
        : undefined,
    thinking: getAnthropicThinkingSettings(
      request.modelSettings.reasoning?.effort,
      maxTokens,
      options,
    ),
    ...providerData,
  };

  return requestData;
}

function buildSystemInstructions(
  system: string,
  outputType: SerializedOutputType,
): string | undefined {
  const parts = system ? [system] : [];
  if (outputType !== 'text') {
    parts.push(
      [
        'Return only valid JSON matching this JSON schema.',
        'Do not wrap the JSON in markdown fences.',
        JSON.stringify(outputType.schema),
      ].join('\n'),
    );
  }
  return parts.length > 0 ? parts.join('\n\n') : undefined;
}

function resolveMaxTokens(
  model: string,
  request: ModelRequest,
  options: AnthropicMessagesModelOptions,
): number {
  const configured =
    request.modelSettings.maxTokens ??
    resolveTokenOption(options.defaultMaxTokens, model) ??
    DEFAULT_ANTHROPIC_MAX_TOKENS;
  const cap = resolveTokenOption(options.maxOutputTokens, model);
  return cap === undefined ? configured : Math.min(configured, cap);
}

function resolveTokenOption(
  value: number | ((model: string) => number | undefined) | undefined,
  model: string,
): number | undefined {
  return typeof value === 'function' ? value(model) : value;
}

function getAnthropicThinkingSettings(
  effort: ModelSettingsReasoningEffort | undefined,
  maxTokens: number,
  options: AnthropicMessagesModelOptions,
): MessageCreateParamsBase['thinking'] | undefined {
  if (effort === undefined) {
    return undefined;
  }
  if (effort === 'none' || effort === null) {
    return { type: 'disabled' };
  }
  const budget =
    options.thinkingBudgetTokens?.[effort] ??
    DEFAULT_THINKING_BUDGET_TOKENS[effort];
  if (budget === undefined || budget <= 0) {
    return undefined;
  }
  return {
    type: 'enabled',
    budget_tokens: Math.min(
      Math.max(1024, budget),
      Math.max(1024, maxTokens - 1),
    ),
  };
}

function itemsToAnthropicMessages(request: ModelRequest): {
  messages: MessageParam[];
  system: string;
} {
  const messages: MessageParam[] = [];
  const systemParts: string[] = [];
  if (request.systemInstructions) {
    systemParts.push(request.systemInstructions);
  }

  if (typeof request.input === 'string') {
    pushContent(messages, 'user', [{ type: 'text', text: request.input }]);
    return { messages, system: systemParts.join('\n\n') };
  }

  for (const item of request.input) {
    if ((item as { type?: unknown }).type === 'compaction') {
      throw new UserError(
        'Responses compaction items are not supported with Anthropic Messages. Use Runner-level local compaction instead.',
      );
    }
    if ('role' in item) {
      if (item.role === 'system') {
        systemParts.push(item.content);
      } else if (item.role === 'user') {
        pushContent(messages, 'user', userContentToAnthropic(item.content));
      } else if (item.role === 'assistant') {
        pushContent(messages, 'assistant', assistantContentToAnthropic(item));
      }
      continue;
    }

    if (item.type === 'reasoning') {
      const replay = getLyraReplayPayload(
        item.providerData as Record<string, unknown> | undefined,
        ANTHROPIC_PROVIDER_NAME,
      );
      if (replay) {
        pushContent(
          messages,
          'assistant',
          replay.blocks as unknown as ContentBlockParam[],
        );
        continue;
      }

      const thinking = item.rawContent?.map((c) => c.text).join('') ?? '';
      if (thinking) {
        pushContent(messages, 'assistant', [
          {
            type: 'thinking',
            thinking,
            signature: getString(item.providerData?.signature) ?? '',
          },
        ]);
      }
      continue;
    }

    if (item.type === 'function_call') {
      pushContent(messages, 'assistant', [
        {
          type: 'tool_use',
          id: item.callId,
          name: item.name,
          input: parseJsonObject(item.arguments),
        },
      ]);
      continue;
    }

    if (item.type === 'function_call_result') {
      pushContent(messages, 'user', [
        {
          type: 'tool_result',
          tool_use_id: item.callId,
          content: functionResultOutputToAnthropicToolResultContent(
            item.output,
          ),
          is_error: item.status === 'incomplete' ? true : undefined,
        } satisfies ToolResultBlockParam,
      ]);
      continue;
    }

    if (item.type === 'unknown' && item.providerData) {
      const role = item.providerData.role;
      const content = item.providerData.content;
      if ((role === 'user' || role === 'assistant') && Array.isArray(content)) {
        pushContent(messages, role, content as ContentBlockParam[]);
      }
      continue;
    }

    if (item.type === 'hosted_tool_call') {
      throw new UserError(
        'Hosted tool calls are not supported with Anthropic Messages.',
      );
    }
  }

  return { messages, system: systemParts.join('\n\n') };
}

function pushContent(
  messages: MessageParam[],
  role: 'user' | 'assistant',
  content: ContentBlockParam[],
): void {
  if (content.length === 0) {
    return;
  }
  const last = messages[messages.length - 1];
  if (last?.role === role && Array.isArray(last.content)) {
    last.content.push(...content);
    return;
  }
  messages.push({ role, content: [...content] });
}

function userContentToAnthropic(
  content: protocol.UserMessageItem['content'],
): ContentBlockParam[] {
  if (typeof content === 'string') {
    return [{ type: 'text', text: content }];
  }
  return content.map((part) => {
    if (part.type === 'input_text') {
      return { type: 'text', text: part.text };
    }
    if (part.type === 'input_image') {
      return inputImageToAnthropic(part);
    }
    throw new UserError(
      'Anthropic Messages does not support this content part.',
    );
  });
}

function inputImageToAnthropic(part: protocol.InputImage): ContentBlockParam {
  if (typeof part.image !== 'string' || part.image.trim().length === 0) {
    throw new UserError(
      'Anthropic image inputs require a URL, data URL, or base64 string.',
    );
  }

  const image = part.image.trim();
  const source = image.startsWith('data:')
    ? dataUrlToAnthropicImageSource(image)
    : stringToAnthropicImageSource(image);
  return {
    type: 'image',
    source,
  };
}

function stringToAnthropicImageSource(
  image: string,
): Extract<ContentBlockParam, { type: 'image' }>['source'] {
  if (/^https?:\/\//i.test(image)) {
    return { type: 'url', url: image };
  }
  return {
    type: 'base64',
    media_type: 'image/jpeg',
    data: image,
  };
}

function dataUrlToAnthropicImageSource(
  image: string,
): Extract<ContentBlockParam, { type: 'image' }>['source'] {
  const match = image.match(/^data:([^;,]+);base64,(.+)$/i);
  if (!match) {
    throw new UserError('Anthropic image data URLs must use base64 encoding.');
  }
  return {
    type: 'base64',
    media_type: match[1]! as 'image/jpeg',
    data: match[2]!,
  };
}

function assistantContentToAnthropic(
  item: protocol.AssistantMessageItem,
): ContentBlockParam[] {
  return item.content.map((part) => {
    if (part.type === 'output_text') {
      return { type: 'text', text: part.text };
    }
    if (part.type === 'refusal') {
      return { type: 'text', text: part.refusal };
    }
    throw new UserError(
      'Anthropic Messages does not support assistant images.',
    );
  });
}

function toolToAnthropic(tool: SerializedTool): ToolUnion {
  if (tool.type !== 'function') {
    throw new UserError(
      `Hosted tools are not supported with Anthropic Messages. Got tool type: ${
        (tool as SerializedHostedTool).type
      }`,
    );
  }
  if (typeof tool.namespace === 'string' && tool.namespace.trim().length > 0) {
    throw new UserError(
      'Namespaced function tools are not supported with Anthropic Messages.',
    );
  }
  return {
    name: tool.name,
    description: tool.description || undefined,
    input_schema: ensureAnthropicInputSchema(tool.parameters),
  } satisfies Tool;
}

function handoffToAnthropic(handoff: SerializedHandoff): ToolUnion {
  return {
    name: handoff.toolName,
    description: handoff.toolDescription || undefined,
    input_schema: ensureAnthropicInputSchema(handoff.inputJsonSchema),
  } satisfies Tool;
}

function ensureAnthropicInputSchema(value: unknown): Tool.InputSchema {
  const record = asRecord(value) ?? {};
  return {
    type: 'object',
    ...record,
  };
}

function getAnthropicToolChoice(
  toolChoice: ModelRequest['modelSettings']['toolChoice'],
  parallelToolCalls: boolean | undefined,
): ToolChoice | undefined {
  const disable_parallel_tool_use =
    parallelToolCalls === false ? true : undefined;
  if (!toolChoice || toolChoice === 'auto') {
    return disable_parallel_tool_use
      ? { type: 'auto', disable_parallel_tool_use }
      : undefined;
  }
  if (toolChoice === 'none') {
    return { type: 'none' };
  }
  if (toolChoice === 'required') {
    return { type: 'any', disable_parallel_tool_use };
  }
  return { type: 'tool', name: toolChoice, disable_parallel_tool_use };
}

function responseToModelResponse(
  response: Message,
  outputType: ModelRequest['outputType'],
): ModelResponse {
  return {
    usage: new Usage(toUsageData(response.usage)),
    output: contentBlocksToOutput(response.content ?? [], response.id, {
      structuredOutput: outputType !== 'text',
    }),
    responseId: response.id,
    providerData: sanitizeLyraReplayProviderData(
      response as unknown as Record<string, unknown>,
    ),
  };
}

function contentBlocksToOutput(
  content: Array<ContentBlock | Record<string, unknown>>,
  responseId?: string,
  options: { structuredOutput?: boolean } = {},
): protocol.OutputModelItem[] {
  const output: protocol.OutputModelItem[] = [];
  const assistantContent: protocol.AssistantContent[] = [];
  const flushAssistantContent = () => {
    if (assistantContent.length === 0) {
      return;
    }
    output.push({
      id: responseId,
      type: 'message',
      role: 'assistant',
      content: normalizeAssistantContentForOutputType(
        assistantContent.splice(0),
        options,
      ),
      status: 'completed',
    });
  };

  for (const block of content) {
    const record = block as Record<string, unknown>;
    if (record.type === 'thinking') {
      flushAssistantContent();
      const thinking = getString(record.thinking) ?? '';
      const replayBlock = {
        type: 'thinking',
        thinking,
        signature: getString(record.signature) ?? '',
      };
      output.push({
        type: 'reasoning',
        content: [],
        rawContent: thinking
          ? [{ type: 'reasoning_text', text: thinking }]
          : undefined,
        providerData: withLyraReplayPayload(record, {
          ...createLyraReplayPayload({
            provider: ANTHROPIC_PROVIDER_NAME,
            kind: 'thinking',
            blocks: [replayBlock],
          }),
        }),
      });
      continue;
    }

    if (record.type === 'redacted_thinking') {
      flushAssistantContent();
      const replayBlock = {
        type: 'redacted_thinking',
        data: getString(record.data) ?? '',
      };
      output.push({
        type: 'reasoning',
        content: [],
        providerData: withLyraReplayPayload(record, {
          ...createLyraReplayPayload({
            provider: ANTHROPIC_PROVIDER_NAME,
            kind: 'redacted_thinking',
            blocks: [replayBlock],
          }),
        }),
      });
      continue;
    }

    if (record.type === 'text') {
      const text = getString(record.text) ?? '';
      if (text) {
        assistantContent.push({
          type: 'output_text',
          text,
          providerData: record,
        });
      }
      continue;
    }

    if (record.type === 'tool_use') {
      flushAssistantContent();
      output.push({
        id: responseId,
        type: 'function_call',
        callId: getString(record.id) ?? '',
        name: getString(record.name) ?? '',
        arguments: JSON.stringify(record.input ?? {}),
        status: 'completed',
        providerData: record,
      });
      continue;
    }

    flushAssistantContent();
    output.push({
      id: responseId,
      type: 'unknown',
      providerData: record,
    });
  }

  flushAssistantContent();

  return output;
}

function normalizeAssistantContentForOutputType(
  content: protocol.AssistantContent[],
  options: { structuredOutput?: boolean },
): protocol.AssistantContent[] {
  if (!options.structuredOutput) {
    return content;
  }
  const textParts = content.filter((part) => part.type === 'output_text');
  if (textParts.length !== content.length || textParts.length === 0) {
    return content;
  }
  const combinedText = textParts.map((part) => part.text).join('');
  const normalizedText = stripSingleJsonMarkdownFence(combinedText);
  if (normalizedText === combinedText) {
    return content;
  }
  return [{ ...textParts[0]!, text: normalizedText }];
}

function stripSingleJsonMarkdownFence(output: string): string {
  const trimmed = output.trim();
  const fencedJson = trimmed.match(
    /^```[ \t]*(?:json|\[json\])?[ \t]*\r?\n([\s\S]*?)\r?\n?```[ \t]*$/i,
  );
  return fencedJson ? fencedJson[1]!.trim() : output;
}

function applyAnthropicStreamEvent(
  state: StreamingState,
  event: Record<string, unknown>,
): ResponseStreamEvent[] {
  const type = event.type;
  if (type === 'content_block_start') {
    const index = Number(event.index ?? 0);
    if (state.activeBlockIndex !== undefined) {
      throw new ModelBehaviorError(
        `Anthropic stream started content block ${index} before content block ${state.activeBlockIndex} stopped.`,
      );
    }
    const contentBlock = asRecord(event.content_block) ?? {};
    const block = getOrCreateStreamingBlock(state, index);
    if (block.partId) {
      throw new ModelBehaviorError(
        `Anthropic stream started duplicate content block ${index}.`,
      );
    }
    block.type = getString(contentBlock.type) ?? 'unknown';
    block.providerData = contentBlock;
    block.partId = getAnthropicOutputPartId(state, block, contentBlock);
    state.activeBlockIndex = index;
    if (block.type === 'text') {
      block.text += getString(contentBlock.text) ?? '';
    } else if (block.type === 'thinking') {
      block.thinking += getString(contentBlock.thinking) ?? '';
      block.signature = getString(contentBlock.signature);
    } else if (block.type === 'redacted_thinking') {
      block.redactedData = getString(contentBlock.data) ?? '';
    } else if (block.type === 'tool_use') {
      block.id = getString(contentBlock.id);
      block.name = getString(contentBlock.name);
      block.input = contentBlock.input;
    }
    const events: ResponseStreamEvent[] =
      block.type === 'redacted_thinking'
        ? []
        : [buildOutputPartStartedEvent(event, block)];
    events.push(...deltasFromInitialBlock(event, block));
    return events;
  }

  if (type === 'content_block_delta') {
    const index = Number(event.index ?? 0);
    const block = getActiveStreamingBlock(state, index, type);
    const delta = asRecord(event.delta) ?? {};
    const deltaType = delta.type;
    if (deltaType === 'text_delta') {
      assertStreamingBlockType(block, 'text', deltaType);
      const text = getString(delta.text) ?? '';
      block.text += text;
      return text
        ? [{ type: 'output_text_delta', delta: text, providerData: event }]
        : [];
    }
    if (deltaType === 'thinking_delta') {
      assertStreamingBlockType(block, 'thinking', deltaType);
      const thinking = getString(delta.thinking) ?? '';
      block.thinking += thinking;
      return thinking
        ? [{ type: 'reasoning_delta', delta: thinking, providerData: event }]
        : [];
    }
    if (deltaType === 'input_json_delta') {
      assertStreamingBlockType(block, 'tool_use', deltaType);
      const inputDelta = getString(delta.partial_json) ?? '';
      block.inputJson += inputDelta;
      return inputDelta
        ? [
            {
              type: 'tool_input_delta',
              partId: requireStreamingBlockPartId(block),
              delta: inputDelta,
              providerData: event,
            },
          ]
        : [];
    }
    if (deltaType === 'signature_delta') {
      assertStreamingBlockType(block, 'thinking', deltaType);
      block.signature = `${block.signature ?? ''}${getString(delta.signature) ?? ''}`;
    }
    return [];
  }

  if (type === 'content_block_stop') {
    const index = Number(event.index ?? 0);
    const block = getActiveStreamingBlock(state, index, type);
    state.activeBlockIndex = undefined;
    return block.type === 'redacted_thinking'
      ? []
      : [
          {
            type: 'output_part_done',
            partId: requireStreamingBlockPartId(block),
            providerData: event,
          },
        ];
  }

  if (type === 'message_delta') {
    mergeUsage(state.usage, asRecord(event.usage));
    return [];
  }

  if (type === 'message_stop') {
    if (state.activeBlockIndex !== undefined) {
      throw new ModelBehaviorError(
        `Anthropic stream stopped message before content block ${state.activeBlockIndex} stopped.`,
      );
    }
    return [buildResponseDoneEvent(state)];
  }

  return [];
}

function deltasFromInitialBlock(
  event: Record<string, unknown>,
  block: StreamingBlock,
): ResponseStreamEvent[] {
  if (block.type === 'text' && block.text) {
    return [
      { type: 'output_text_delta', delta: block.text, providerData: event },
    ];
  }
  if (block.type === 'thinking' && block.thinking) {
    return [
      { type: 'reasoning_delta', delta: block.thinking, providerData: event },
    ];
  }
  if (block.type === 'tool_use' && hasNonEmptyToolInput(block.input)) {
    return [
      {
        type: 'tool_input_delta',
        partId: requireStreamingBlockPartId(block),
        delta: JSON.stringify(block.input),
        providerData: event,
      },
    ];
  }
  return [];
}

function buildOutputPartStartedEvent(
  event: Record<string, unknown>,
  block: StreamingBlock,
): protocol.StreamEventOutputPartStarted {
  const partId = requireStreamingBlockPartId(block);
  if (block.type === 'text') {
    return {
      type: 'output_part_started',
      partId,
      part: { type: 'output_text' },
      providerData: event,
    };
  }
  if (block.type === 'thinking') {
    return {
      type: 'output_part_started',
      partId,
      part: { type: 'reasoning' },
      providerData: event,
    };
  }
  if (block.type === 'tool_use') {
    if (!block.id || !block.name) {
      throw new ModelBehaviorError(
        `Anthropic stream started tool_use block ${block.index} without id and name.`,
      );
    }
    return {
      type: 'output_part_started',
      partId,
      part: { type: 'function_call', callId: block.id, name: block.name },
      providerData: event,
    };
  }
  throw new ModelBehaviorError(
    `Anthropic stream started unsupported content block type ${block.type}.`,
  );
}

function getAnthropicOutputPartId(
  state: StreamingState,
  block: StreamingBlock,
  contentBlock: Record<string, unknown>,
): string {
  if (block.type === 'tool_use') {
    const id = getString(contentBlock.id);
    if (id) {
      return id;
    }
  }
  return `${state.id}:content_block:${block.index}`;
}

function getActiveStreamingBlock(
  state: StreamingState,
  index: number,
  eventType: unknown,
): StreamingBlock {
  if (state.activeBlockIndex !== index) {
    throw new ModelBehaviorError(
      `Anthropic stream received ${String(eventType)} for content block ${index} while content block ${String(
        state.activeBlockIndex,
      )} is active.`,
    );
  }
  const block = state.blocks.get(index);
  if (!block?.partId) {
    throw new ModelBehaviorError(
      `Anthropic stream received ${String(eventType)} for unknown content block ${index}.`,
    );
  }
  return block;
}

function assertStreamingBlockType(
  block: StreamingBlock,
  expectedType: string,
  deltaType: unknown,
): void {
  if (block.type !== expectedType) {
    throw new ModelBehaviorError(
      `Anthropic stream received ${String(deltaType)} for ${block.type} content block ${block.index}.`,
    );
  }
}

function requireStreamingBlockPartId(block: StreamingBlock): string {
  if (!block.partId) {
    throw new ModelBehaviorError(
      `Anthropic stream content block ${block.index} has no part id.`,
    );
  }
  return block.partId;
}

function hasNonEmptyToolInput(input: unknown): boolean {
  if (input === undefined || input === null) {
    return false;
  }
  if (typeof input === 'object' && !Array.isArray(input)) {
    return Object.keys(input as Record<string, unknown>).length > 0;
  }
  return true;
}

function buildResponseDoneEvent(
  state: StreamingState,
): protocol.StreamEventResponseCompleted {
  const blocks = Array.from(state.blocks.values()).sort(
    (left, right) => left.index - right.index,
  );
  return {
    type: 'response_done',
    response: {
      id: state.id,
      usage: toUsageData(state.usage),
      output: streamingBlocksToOutput(blocks, state.id, {
        structuredOutput: state.structuredOutput,
      }),
    },
  };
}

function streamingBlocksToOutput(
  blocks: StreamingBlock[],
  responseId: string,
  options: { structuredOutput?: boolean } = {},
): protocol.OutputModelItem[] {
  const contentBlocks = blocks.map((block) => {
    if (block.type === 'thinking') {
      return {
        type: 'thinking',
        thinking: block.thinking,
        signature: block.signature,
      };
    }
    if (block.type === 'redacted_thinking') {
      return {
        type: 'redacted_thinking',
        data: block.redactedData ?? '',
      };
    }
    if (block.type === 'text') {
      return { type: 'text', text: block.text };
    }
    if (block.type === 'tool_use') {
      return {
        type: 'tool_use',
        id: block.id,
        name: block.name,
        input: parseStreamingToolInput(block),
      };
    }
    return {
      type: block.type,
      providerData: block.providerData,
    };
  });
  return contentBlocksToOutput(contentBlocks, responseId, options);
}

function getOrCreateStreamingBlock(
  state: StreamingState,
  index: number,
): StreamingBlock {
  let block = state.blocks.get(index);
  if (!block) {
    block = {
      index,
      type: 'unknown',
      text: '',
      thinking: '',
      inputJson: '',
    };
    state.blocks.set(index, block);
  }
  return block;
}

function parseStreamingToolInput(block: StreamingBlock): unknown {
  if (block.inputJson.trim().length > 0) {
    return parseJsonObject(block.inputJson);
  }
  return block.input ?? {};
}

function toUsageData(usage?: Partial<AnthropicUsage>): protocol.UsageData {
  const inputTokens = Number(usage?.input_tokens ?? 0);
  const outputTokens = Number(usage?.output_tokens ?? 0);
  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    inputTokensDetails: {
      cache_creation_input_tokens: Number(
        usage?.cache_creation_input_tokens ?? 0,
      ),
      cache_read_input_tokens: Number(usage?.cache_read_input_tokens ?? 0),
    },
    outputTokensDetails: {},
  };
}

function mergeUsage(
  target: Partial<AnthropicUsage>,
  source: Record<string, unknown> | undefined,
): void {
  if (!source) {
    return;
  }
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === 'number') {
      (target as Record<string, number>)[key] = value;
    }
  }
}

function parseJsonObject(value: string): unknown {
  try {
    return JSON.parse(value || '{}');
  } catch {
    return value;
  }
}

function functionResultOutputToText(
  output: protocol.FunctionCallResultItem['output'],
): string {
  if (typeof output === 'string') {
    return output;
  }
  if (Array.isArray(output)) {
    return output
      .map((part) =>
        part.type === 'input_text' ? part.text : JSON.stringify(part),
      )
      .join('\n');
  }
  if (output.type === 'text') {
    return output.text;
  }
  return JSON.stringify(output);
}

function functionResultOutputToAnthropicToolResultContent(
  output: protocol.FunctionCallResultItem['output'],
): ToolResultBlockParam['content'] {
  if (typeof output === 'string') {
    return output;
  }
  if (Array.isArray(output)) {
    return output.map((part) => {
      if (part.type === 'input_text') {
        return { type: 'text', text: part.text };
      }
      if (part.type === 'input_image') {
        return inputImageToAnthropic(part);
      }
      return { type: 'text', text: JSON.stringify(part) };
    }) as ToolResultBlockParam['content'];
  }
  if (output.type === 'text') {
    return output.text;
  }
  if (output.type === 'image') {
    return [
      inputImageToAnthropic(legacyImageOutputToInputImage(output)),
    ] as ToolResultBlockParam['content'];
  }
  return functionResultOutputToText(output);
}

function legacyImageOutputToInputImage(
  output: protocol.ToolOutputImage,
): protocol.InputImage {
  const image = output.image;
  if (typeof image === 'string') {
    return {
      type: 'input_image',
      image,
      providerData: output.providerData,
    };
  }
  if (image && typeof image === 'object') {
    if ('url' in image && typeof image.url === 'string') {
      return {
        type: 'input_image',
        image: image.url,
        providerData: output.providerData,
      };
    }
    if ('data' in image) {
      const mediaType =
        typeof image.mediaType === 'string' ? image.mediaType : 'image/jpeg';
      const data =
        typeof image.data === 'string'
          ? image.data
          : Buffer.from(image.data).toString('base64');
      return {
        type: 'input_image',
        image: data.startsWith('data:')
          ? data
          : `data:${mediaType};base64,${data}`,
        providerData: output.providerData,
      };
    }
  }
  throw new UserError(
    `Anthropic Messages image tool outputs require string image data, data objects, or URLs: ${JSON.stringify(output)}`,
  );
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}
